import React, { useEffect, useState } from 'react';
import * as Sentry from '@sentry/nextjs';
import { Elements } from '@stripe/react-stripe-js';
import { Stripe } from '@stripe/stripe-js';
import { getTournamentPartnerRequestApi } from 'constants/payloads/tournamentPartnerRequest';
import {
  EventCouponSourceEnum,
  PaymentFulfillmentChannelsEnum,
  useGetEventCouponsQuery,
  useGetUserEventRegistrationLazyQuery,
} from 'types/generated/client';
import { Response } from 'services/client/stripe/getSetupIntent';
import { useApiGateway } from 'hooks/useApi';
import {
  EventCoupon,
  OrderTotal,
  calculateEventOrderTotal,
} from 'utils/shared/money/calculateEventOrderTotal';
import { useGetCurrentUser } from 'hooks/useGetCurrentUser';
import ConfirmWithoutPayment from './ConfirmWithoutPayment';
import Header from './Header';
import LoadingSkeleton from './LoadingSkeleton';
import Payment from './Payment';
import { TournamentPaymentChoice } from './PaymentMethodChoice';
import PaymentMethodSelector from './PaymentMethodSelector';
import { RegistrationFormData } from './types';
import { EventProps, Steps } from '../types';

function isCouponEligible(c: EventCoupon): boolean {
  if (!c.isActive) return false;
  const now = Date.now();
  // not used for mvp
  // if (c.validFrom && new Date(c.validFrom).getTime() > now) return false;
  if (c.validUntil && new Date(c.validUntil).getTime() < now) return false;
  if (c.maxRedemptions !== null && c.maxRedemptions !== undefined) {
    const redemptionCount = c.redemptionsAggregate?.aggregate?.count ?? 0;
    if (redemptionCount >= c.maxRedemptions) return false;
  }
  return true;
}

interface PaymentStepProps extends EventProps {
  stripe: Stripe | null;
  stripeKeys: Response | undefined;
  fetchSetupIntent: () => Promise<void>;
  setSteps: React.Dispatch<React.SetStateAction<Steps>>;
  registrationFormData: RegistrationFormData;
  orderTotal: OrderTotal;
  isFirstTournamentRegistration: boolean;
  tournamentPaymentChoice: TournamentPaymentChoice | null;
  setTournamentPaymentChoice: React.Dispatch<React.SetStateAction<TournamentPaymentChoice | null>>;
  isIgnoreInvitation: boolean;
  invitationId: string | undefined;
  teamId: string | undefined;
  userId: string | null | undefined;
  isPrivateWithOutCost: boolean;
  onEventDataChanged?: () => void;
}

export default function PaymentStep({
  event,
  stripe,
  stripeKeys,
  fetchSetupIntent,
  setSteps,
  registrationFormData,
  orderTotal: orderTotalWithoutCoupon,
  isFirstTournamentRegistration,
  tournamentPaymentChoice,
  setTournamentPaymentChoice,
  isIgnoreInvitation,
  invitationId,
  teamId,
  userId,
  isPrivateWithOutCost,
  onEventDataChanged,
}: PaymentStepProps) {
  const { user } = useGetCurrentUser();
  const [getUserEventRegistrationLazyQuery] = useGetUserEventRegistrationLazyQuery();
  const [appliedCoupon, setAppliedCoupon] = useState<EventCoupon | null>(null);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [isEarlyBirdApplied, setIsEarlyBirdApplied] = useState(false);
  const [isEarlyBirdDismissed, setIsEarlyBirdDismissed] = useState(false);

  const { data: couponsData } = useGetEventCouponsQuery({
    variables: { eventId: event.id },
    skip: !event.id,
  });

  const allCoupons = couponsData?.eventCoupons ?? [];
  const registeredGroupIds = new Set(registrationFormData.groups.map((g) => g.groupId));

  const appliesToRegisteredGroups = (c: EventCoupon): boolean => {
    if (c.couponGroups.length === 0) return true;
    return c.couponGroups.some((cg) => registeredGroupIds.has(cg.eventGroupId));
  };

  // One early bird per event is expected — find the first eligible one that applies to registered groups
  const earlyBirdCoupon =
    allCoupons.find(
      (c) =>
        c.source === EventCouponSourceEnum.EarlyBird &&
        c.isAutoApply &&
        isCouponEligible(c) &&
        appliesToRegisteredGroups(c),
    ) ?? null;

  // Auto-apply early bird once coupons have loaded (unless user dismissed it)
  useEffect(() => {
    if (earlyBirdCoupon && !appliedCoupon && !isEarlyBirdDismissed) {
      setAppliedCoupon(earlyBirdCoupon);
      setIsEarlyBirdApplied(true);
    }
  }, [earlyBirdCoupon?.id]);

  const availableManualCoupons = allCoupons.filter(
    (c) =>
      c.source === EventCouponSourceEnum.Manual &&
      isCouponEligible(c) &&
      appliesToRegisteredGroups(c),
  );

  const hasManualCoupons = availableManualCoupons.length > 0;

  const handleApplyCoupon = (code: string) => {
    setCouponError(null);
    const match = availableManualCoupons.find((c) => c.code.toLowerCase() === code.toLowerCase());
    if (match) {
      setAppliedCoupon(match);
      setIsEarlyBirdApplied(false);
    } else {
      setCouponError('Invalid or expired discount code.');
    }
  };

  const handleClearCoupon = () => {
    // If clearing a manual code, restore early bird if still eligible
    if (!isEarlyBirdApplied && earlyBirdCoupon) {
      setAppliedCoupon(earlyBirdCoupon);
      setIsEarlyBirdApplied(true);
    } else {
      setAppliedCoupon(null);
      setIsEarlyBirdApplied(false);
      setIsEarlyBirdDismissed(true);
    }
    setCouponError(null);
  };

  const orderTotal = appliedCoupon
    ? calculateEventOrderTotal({
        event,
        registration: registrationFormData.groups,
        isFirstTournamentRegistration,
        coupon: appliedCoupon,
      })
    : orderTotalWithoutCoupon;

  const showPaymentForm = !isPrivateWithOutCost && tournamentPaymentChoice !== 'at_event';

  const cameFromPartnerSetup = registrationFormData.groups.some((g) => g.hasPartner === true);
  const handleBack = () => setSteps(cameFromPartnerSetup ? Steps.PartnerSetup : Steps.AddEvent);
  const handleNext = () => setSteps(Steps.AddEvent);

  const { post: sendPartnerRequest } = useApiGateway<{
    groupRegistrationId: string;
    groupId: string;
    invitedUserId: string;
  }>(getTournamentPartnerRequestApi(event.id));

  const handleSubmitSuccess = async () => {
    // Fire in-event partner requests after registration
    const inEventGroups = registrationFormData.groups.filter(
      (g) => g.partnerType === 'in-event' && g.partnerId,
    );

    if (inEventGroups.length > 0 && userId) {
      try {
        const { data } = await getUserEventRegistrationLazyQuery({
          fetchPolicy: 'network-only',
          variables: { userId, eventId: event.id },
        });

        if (data?.eventGroupRegistrations) {
          await Promise.all(
            inEventGroups.map((group) => {
              const registration = data.eventGroupRegistrations.find(
                (egr) => egr.group.id === group.groupId,
              );
              if (registration && group.partnerId) {
                return sendPartnerRequest({
                  payload: {
                    groupRegistrationId: registration.id,
                    groupId: group.groupId,
                    invitedUserId: group.partnerId,
                  },
                }).catch((e) => Sentry.captureException(e));
              }
              return Promise.resolve();
            }),
          );
        }
      } catch (e) {
        Sentry.captureException(e);
      }
    } else if (userId) {
      await getUserEventRegistrationLazyQuery({
        fetchPolicy: 'network-only',
        variables: { userId, eventId: event.id },
      });
    }

    onEventDataChanged?.();
    setSteps(Steps.AddEvent);
    if (showPaymentForm) {
      fetchSetupIntent();
    }
  };

  const header = (
    <Header
      title="Pay and confirm"
      showBack={true}
      showBackAction={handleBack}
      className="sticky top-0 z-[10] bg-color-bg-lightmode-primary px-6 pb-5 pt-6 dark:bg-color-bg-darkmode-primary"
    />
  );

  const sharedProps = {
    invitationId: isIgnoreInvitation ? '' : invitationId,
    teamId: isIgnoreInvitation ? '' : teamId,
    registrationFormData,
    setSteps,
    event,
    user,
    orderTotal,
    handleNext,
    onSubmit: handleSubmitSuccess,
    back: handleBack,
    appliedCoupon,
    couponError,
    onApplyCoupon: hasManualCoupons ? handleApplyCoupon : undefined,
    onClearCoupon: handleClearCoupon,
  };

  const paymentMethodSelector =
    event.paymentFulfillmentChannel === PaymentFulfillmentChannelsEnum.ParticipantsChoice ? (
      <div className="px-6 pt-1">
        <PaymentMethodSelector
          selected={tournamentPaymentChoice === 'at_event' ? 'at_event' : 'card'}
          onSelectCard={() => setTournamentPaymentChoice('online')}
          onSelectPayAtEvent={() => setTournamentPaymentChoice('at_event')}
        />
      </div>
    ) : null;

  if (showPaymentForm) {
    if (!stripeKeys?.setupIntentClientSecret) {
      return <LoadingSkeleton />;
    }

    return (
      <div className="tournament-register-form overflow-auto">
        {header}
        {paymentMethodSelector}
        <Elements
          stripe={stripe}
          options={{
            clientSecret: stripeKeys.setupIntentClientSecret,
            appearance: {
              labels: undefined,
            },
          }}
        >
          <Payment {...sharedProps} />
        </Elements>
      </div>
    );
  }

  return (
    <div className="tournament-register-form overflow-auto">
      {header}
      {paymentMethodSelector}
      <ConfirmWithoutPayment {...sharedProps} />
    </div>
  );
}
